import { SecretModal, useInactivityTimer, UserActivityModal } from '@echotex/business-modules';
import { ConfigurationForm } from '../ConfigurationForm';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { SchemaGenerator } from './Schema';
import { useSchema, useForm } from '@core-web/hooks';
import { addSessionCookie, deleteSessionCookie, serialize } from '@core-web/lib';
import { ConfigurationKey, getConfigurations } from '../../helpers';
import { EventTypes } from '../../types';
import { useDispatch } from 'react-redux';
import { useVerifyUser } from '@apps/auth';
import { useLogoutUser } from '@echotex/api-react';

enum ModalTypes {
  Configuration = 'Configuration',
  UserActivity = 'UserActivity',
}

export const AppConfiguration = (): JSX.Element => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [keyPressCount, setKeyPressCount] = useState(0);
  const [modalType, setModalType] = useState<ModalTypes | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prevConfiguration = getConfigurations();
  const { isAuthenticated } = useVerifyUser();
  const dispatch = useDispatch();

  // api hooks
  const { callApi } = useLogoutUser();

  const handleOnTimeOut = useCallback(() => {
    setModalType(ModalTypes.UserActivity);
  }, []);

  // activity hook
  const { startTimer } = useInactivityTimer({
    timeout: 120 * 60 * 1000, // 120 minutes
    onTimeout: handleOnTimeOut,
    autoStartDisabled: true, // Automatically disable the timer start
  });

  const schema = useSchema(SchemaGenerator(prevConfiguration));
  const { fieldRules, defaultValues } = schema;
  const methods = useForm(fieldRules, {
    defaultValues,
  });

  // lifecycle hooks
  useEffect(() => {
    const hostname = window.location.hostname;

    if (hostname.search('v2.echosys') === -1) {
      window.addEventListener('keydown', handleKeyDown);

      dispatch({
        type: EventTypes.SET_CONFIGURATION,
        payload: prevConfiguration,
      });

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      startTimer();
    }
  }, [isAuthenticated]);

  // handler methods
  const handleKeyDown = (event: any) => {
    if (event.key === 'c' || event.key === 'C') {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setKeyPressCount((prevKeyPressCount) => {
        const totalKeyPressCount = prevKeyPressCount + 1;
        if (totalKeyPressCount === 3) {
          setModalType(ModalTypes.Configuration);
        }
        return totalKeyPressCount;
      });
      timeoutRef.current = setTimeout(() => {
        setKeyPressCount(0);
      }, 3000);
    } else {
      setKeyPressCount(0);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }
  };

  const handleConfigurationConfirmButtonClick = () => {
    const formValues = methods.getValues();

    if (formValues?.environment?.label !== prevConfiguration?.environment?.label) {
      deleteSessionCookie('auth');
    }

    addSessionCookie(ConfigurationKey, serialize(formValues));
    window.location.reload();
  };

  const handleConfigurationCancelButtonClick = () => {
    setModalType(null);
    setKeyPressCount(0);
  };

  const handleActivityConfirm = () => {
    setModalType(null);
    startTimer();
  };

  const handleActivityCancel = () => {
    callApi({});
    setModalType(null);
  };

  const renderModal = () => {
    switch (modalType) {
      case ModalTypes.Configuration:
        return (
          <SecretModal
            handleConfirmButtonClick={handleConfigurationConfirmButtonClick}
            handleCancelButtonClick={handleConfigurationCancelButtonClick}
            title={'App Configuration'}>
            <FormProvider {...methods}>
              <ConfigurationForm schema={schema} />
            </FormProvider>
          </SecretModal>
        );
      case ModalTypes.UserActivity:
        return <UserActivityModal onConfirm={handleActivityConfirm} onCancel={handleActivityCancel} />;
      default:
        return null;
    }
  };

  return <>{renderModal()}</>;
};
